import { UseFormReturnType } from "@mantine/form"
import { useTranslation } from "react-i18next"

import { CountrySelectField } from "~/pages/Admin/components/Fields/CountrySelectField"
import { DateInputField } from "~/pages/Admin/components/Fields/DateInputField"
import { LanguageSelectField } from "~/pages/Admin/components/Fields/LanguageSelectField"
import { MultiSelectField } from "~/pages/Admin/components/Fields/MultiSelectField"
import { BusinessPartnerOrganizationAutocompleteField } from "~/pages/Admin/components/Fields/BusinessPartnerRepositoryAutocomplete/BusinessPartnerOrganizationAutocompleteField"
import { ReferenceInputField } from "~/pages/Admin/components/Fields/ReferenceField"
import { SelectField } from "~/pages/Admin/components/Fields/SelectField"
import SelectListRegistrationId from "~/pages/Admin/components/Fields/SelectListRegistrationId"
import { SwitchField } from "~/pages/Admin/components/Fields/SwitchField"
import { TagsInputField } from "~/pages/Admin/components/Fields/TagsInputField"
import { TextInputField } from "~/pages/Admin/components/Fields/TextInputField"
import { FormFieldConfig } from "~/pages/Admin/components/form.types"
import {
  errorCodes,
  errorCodesEnum,
} from "~/pages/SupplierPortal/TraceabilityRequests/components/ZodCustomErrorMap"

import { CoordinatesInputField } from "./Fields/CoordinatesInputField"

const isErrorCodeKey = (key: string): key is keyof errorCodes => {
  return Object.keys(errorCodesEnum).includes(key)
}
const getErrorWord = <T extends Record<string, unknown>>(
  form: UseFormReturnType<T>,
  fields: FormFieldConfig
): keyof errorCodes | undefined => {
  const raw = form.errors[fields.name]
  const message = typeof raw === "string" ? raw : undefined
  if (!message) {
    return undefined
  }
  if (isErrorCodeKey(message)) {
    return message
  }
  return undefined
}

type RenderFieldProps<T extends Record<string, unknown>> = {
  field: FormFieldConfig
  form: UseFormReturnType<T>
  readonly?: boolean
}

const INPUTS = {
  text: TextInputField,
  select: SelectField,
  multiselect: MultiSelectField,
  countrySelect: CountrySelectField,
  languageSelect: LanguageSelectField,
  tagsInput: TagsInputField,
  selectListRegistrationId: SelectListRegistrationId,
  date: DateInputField,
  switch: SwitchField,
  reference: ReferenceInputField,
  coordinates: CoordinatesInputField,
  default: () => null,
}

const getField = <T extends Record<string, unknown>>(
  field: FormFieldConfig,
  form: UseFormReturnType<T>,
  error?: keyof errorCodes,
  translations?: errorCodes
): React.ReactNode => {
  // Render as JSX so this field’s hooks belong to its own fiber (not RenderField).
  if (field.type === "businessPartnerOrganizationAutocomplete") {
    return (
      <BusinessPartnerOrganizationAutocompleteField
        field={field}
        form={form as UseFormReturnType<Record<string, unknown>>}
      />
    )
  }

  const inputRenderer =
    INPUTS[field.type as keyof typeof INPUTS] ?? INPUTS.default

  return (
    inputRenderer(
      field,
      form as UseFormReturnType<Record<string, unknown>>,
      error,
      translations
    ) || INPUTS.default()
  )
}

export const RenderField = <T extends Record<string, unknown>>({
  field,
  form,
}: RenderFieldProps<T>) => {
  const { t } = useTranslation("common")
  const error: keyof errorCodes | undefined = getErrorWord(form, field)

  const translations = t(`zodErrors`, {
    returnObjects: true,
  }) as unknown as errorCodes

  return getField(field, form, error, translations)
}
